import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomOperatorPolicies, CustomOperatorPoliciesIDs, OperatorDrawerNavs, OperatorRoles, } from '@ibk/shared-types';
import { RestrictedPoliciesRelationship } from './RegisterType.mocks';
const initialRestrictedPoliciesState = [CustomOperatorPoliciesIDs.TRANSACTION_LIMIT];
export const useRegisterHook = (t, testId, handleGoTo) => {
    const [selectedId, setSelectedId] = useState(undefined);
    const [restrictedPolicies, setRestrictedPolicies] = useState(initialRestrictedPoliciesState);
    const [customValues, setCustomValues] = useState({
        [CustomOperatorPoliciesIDs.TRANSACTION_LIMIT]: undefined,
    });
    const [typedOnCustomValue, setTypedOnCustomValue] = useState({
        [CustomOperatorPoliciesIDs.TRANSACTION_LIMIT]: false,
    });
    const previousSelectedId = useRef(selectedId);
    useEffect(() => {
        return () => {
            setSelectedId(undefined);
            setRestrictedPolicies(initialRestrictedPoliciesState);
            setCustomValues({ [CustomOperatorPoliciesIDs.TRANSACTION_LIMIT]: undefined });
            setTypedOnCustomValue({ [CustomOperatorPoliciesIDs.TRANSACTION_LIMIT]: false });
        };
    }, []);
    useEffect(() => {
        if (previousSelectedId.current !== selectedId) {
            setRestrictedPolicies(initialRestrictedPoliciesState);
        }
        previousSelectedId.current = selectedId;
    }, [selectedId]);
    const handleCustomInput = useCallback((policyId, value) => {
        setCustomValues(prev => ({ ...prev, [policyId]: value }));
        setTypedOnCustomValue(prev => ({ ...prev, [policyId]: true }));
    }, [setCustomValues, setTypedOnCustomValue]);
    const handleCustomInputInnerError = useCallback((policy) => selectedId === OperatorRoles.OPERATOR &&
        !restrictedPolicies.includes(policy) &&
        (!customValues[policy] || (customValues[policy] || 0) < 0.01), [customValues, restrictedPolicies, selectedId]);
    const handleCustomInputError = useCallback((policy) => handleCustomInputInnerError(policy) && typedOnCustomValue[policy], [handleCustomInputInnerError, typedOnCustomValue]);
    const operatorsItems = useMemo(() => {
        const baseArray = [
            {
                id: CustomOperatorPoliciesIDs.ACCOUNT_MANAGER,
                title: t(`${testId}.operator.pj-account.title`),
                text: t(`${testId}.operator.pj-account.description`),
                restrictedPolicies: [
                    CustomOperatorPolicies.ACCOUNT_MANAGER,
                    CustomOperatorPolicies.INSTRUCTION_OPERATOR,
                    CustomOperatorPolicies.ACCOUNT_PAYABLES_MANAGER,
                ],
            },
            {
                id: CustomOperatorPoliciesIDs.BILLING_MANAGER,
                title: t(`${testId}.operator.billing.title`),
                text: t(`${testId}.operator.billing.description`),
                restrictedPolicies: [CustomOperatorPolicies.BILLING_MANAGER],
            },
            {
                id: CustomOperatorPoliciesIDs.BENEFITS_MANAGER,
                title: t(`${testId}.operator.benefits.title`),
                text: t(`${testId}.operator.benefits.description`),
                restrictedPolicies: [CustomOperatorPolicies.BENEFITS_MANAGER],
            },
        ];
        baseArray.splice(1, 0, {
            id: CustomOperatorPoliciesIDs.TRANSACTION_LIMIT,
            title: t(`${testId}.operator.transaction-limit.title`),
            text: t(`${testId}.operator.transaction-limit.description`),
            restrictedPolicies: [CustomOperatorPolicies.TRANSACTION_LIMIT],
            preRequisite: CustomOperatorPoliciesIDs.ACCOUNT_MANAGER,
            customValueFunction: handleCustomInput,
            customValueError: handleCustomInputError(CustomOperatorPoliciesIDs.TRANSACTION_LIMIT),
        });
        return baseArray;
    }, [t, testId, handleCustomInput, handleCustomInputError]);
    const managingPartnerItems = [
        t(`${testId}.managing_partner.item1`),
        t(`${testId}.managing_partner.item2`),
        t(`${testId}.managing_partner.item3`),
        t(`${testId}.managing_partner.item4`),
        t(`${testId}.managing_partner.item5`),
    ];
    const handleSelectItem = useCallback((operatorType) => {
        setSelectedId(operatorType);
    }, [setSelectedId]);
    const handleSelectPolicy = useCallback((policyID) => {
        if (restrictedPolicies.includes(policyID)) {
            setRestrictedPolicies(restrictedPolicies.filter(id => id !== policyID));
            return;
        }
        const isPreRequisiteOf = operatorsItems.filter(item => item.preRequisite === policyID);
        if (isPreRequisiteOf.length > 0) {
            setRestrictedPolicies([...new Set([...restrictedPolicies, policyID, ...isPreRequisiteOf.map(pre => pre.id)])]);
            const updatedCustomValues = { ...customValues };
            const updatedTypedCustomValues = { ...typedOnCustomValue };
            isPreRequisiteOf.forEach(item => {
                updatedCustomValues[item.id] = undefined;
                updatedTypedCustomValues[item.id] = false;
            });
            setCustomValues(updatedCustomValues);
            setTypedOnCustomValue(updatedTypedCustomValues);
            return;
        }
        if (policyID === CustomOperatorPoliciesIDs.TRANSACTION_LIMIT) {
            setCustomValues(prev => ({ ...prev, [policyID]: undefined }));
            setTypedOnCustomValue({ [policyID]: false });
        }
        setRestrictedPolicies([...restrictedPolicies, policyID]);
    }, [restrictedPolicies, operatorsItems, customValues, typedOnCustomValue]);
    const isDisabled = !selectedId ||
        (selectedId === OperatorRoles.OPERATOR &&
            Object.keys(customValues).some(key => handleCustomInputInnerError(key)));
    const handleNext = useCallback(() => {
        if (isDisabled)
            return;
        const params = {
            role: selectedId,
        };
        if (selectedId === OperatorRoles.OPERATOR) {
            const restrictedPoliciesComplete = restrictedPolicies
                .filter(policy => operatorsItems.find(item => item.id === policy))
                .flatMap(item => RestrictedPoliciesRelationship[item]);
            params.restrictedPolicies = restrictedPoliciesComplete.filter(policy => policy !== CustomOperatorPolicies.TRANSACTION_LIMIT);
            if (!restrictedPoliciesComplete.includes(CustomOperatorPolicies.TRANSACTION_LIMIT)) {
                params.transactionLimit = customValues[CustomOperatorPolicies.TRANSACTION_LIMIT];
            }
        }
        handleGoTo(OperatorDrawerNavs.REGISTER_FORM, params);
    }, [isDisabled, selectedId, customValues, handleGoTo, restrictedPolicies, operatorsItems]);
    const states = {
        selectedId,
        restrictedPolicies,
        customValues,
        nextDisabled: isDisabled,
    };
    const actions = {
        handleSelectItem,
        handleSelectPolicy,
        handleNext,
    };
    const items = {
        operatorsItems,
        managingPartnerItems,
    };
    return {
        states,
        actions,
        items,
    };
};
